import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Waves, Wifi, Utensils, Car, Wind, Trees, Bed, Bath, Sun, ShieldCheck, Mountain, ArrowRight, Baby } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import MeetFlorencia from "@/components/MeetFlorencia";
import BookingWidget from "@/components/BookingWidget";
import Lightbox from "@/components/Lightbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CASA, GUIDE } from "@/lib/media";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useSeo } from "@/lib/seo";

const EASE = [0.22, 1, 0.36, 1];

function Reveal({ children, className = "", delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: EASE, delay }} className={className}>
      {children}
    </motion.div>
  );
}
const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, ease: EASE, delay }} className="block">{children}</motion.span>
  </span>
);

function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 150]);
  return (
    <section ref={ref} data-testid="casa-hero" className="relative h-[100svh] overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={CASA.hero} alt="Casa de León grand terrace with Sea of Cortez view" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20" />
      </motion.div>
      <div className="relative z-10 h-full flex flex-col justify-end pb-14 sm:pb-20 lg:pb-28 px-6 lg:px-12 max-w-[1400px] mx-auto">
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="overline text-white/80 mb-4 sm:mb-6 flex items-center gap-3">
          <MapPin className="w-4 h-4" strokeWidth={1.5} /> {t("El Tezal · Cabo San Lucas · Baja California Sur")}
        </motion.span>
        <h1 className="font-display font-light text-white text-[2.65rem] sm:text-7xl lg:text-[6rem] leading-[1.03] sm:leading-[0.92] tracking-tight max-w-5xl [text-shadow:0_2px_24px_rgba(0,0,0,0.4)]">
          <MaskLine delay={0.25}>{t("A Tuscan villa above the")}</MaskLine>
          <MaskLine delay={0.4}><span className="italic">{t("Land's End")}</span>.</MaskLine>
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}
          className="text-white font-normal text-base sm:text-lg lg:text-xl max-w-xl mt-5 sm:mt-8 leading-relaxed [text-shadow:0_1px_16px_rgba(0,0,0,0.55)]">
          {t("Casa de León — a private three-bedroom villa inside the gated community of Punta Arena, where Tuscan warmth meets the endless blue of Cabo San Lucas.")}
        </motion.p>
        <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.9 }}
          data-testid="casa-hero-cta" onClick={() => document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" })}
          className="mt-8 sm:mt-10 self-start px-8 py-3.5 sm:px-9 sm:py-4 bg-[#F6F4F0] text-[#1A1918] tracking-wide rounded-sm hover:bg-white transition-colors">
          {t("Check Availability")}
        </motion.button>
      </div>
    </section>
  );
}

function Story() {
  const { t } = useLang();
  return (
    <section id="residence" className="px-6 lg:px-12 py-28 lg:py-36 max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
      <Reveal>
        <span className="overline text-[#B86B52]">{t("The Villa")}</span>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 text-[#1A1918]">
          {t("Named for the lion, built for the sun")}
        </h2>
        <div className="space-y-4 mt-7 text-[#5C5A56] font-light leading-relaxed">
          <p>{t("Casa de León is a high-end, Tuscan-inspired villa — a home imagined as a true vacation retreat, with soft archways, warm stone tones and light that pours through every room from dawn to dusk.")}</p>
          <p>{t("Three bedrooms, three baths and generous open living flow effortlessly onto a grand terrace with sweeping views. It is a house made for long lunches, slow mornings and evenings that end under the stars.")}</p>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <img src={CASA.livingTerrace} alt="Casa de León living opening to the terrace" className="w-full aspect-[4/5] object-cover rounded-sm" />
      </Reveal>
    </section>
  );
}

function Gallery() {
  const { t } = useLang();
  const items = [
    { src: CASA.grandTerrace, span: "md:col-span-7", ratio: "aspect-[16/10]", label: "Grand Terrace" },
    { src: CASA.living, span: "md:col-span-5", ratio: "aspect-[16/10]", label: "Living & Terrace" },
    { src: CASA.interiorLiving, span: "md:col-span-4", ratio: "aspect-[4/5]", label: "Living Room" },
    { src: CASA.bedroom1, span: "md:col-span-4", ratio: "aspect-[4/5]", label: "Garden Bedroom" },
    { src: CASA.interiorDining, span: "md:col-span-4", ratio: "aspect-[4/5]", label: "Dining Room" },
    { src: CASA.tvLounge, span: "md:col-span-6", ratio: "aspect-[16/10]", label: "Living Lounge" },
    { src: CASA.terrace, span: "md:col-span-6", ratio: "aspect-[16/10]", label: "Terrace Dining" },
    { src: CASA.stairway, span: "md:col-span-6", ratio: "aspect-[16/10]", label: "Interior Stairway" },
    { src: CASA.livingKitchen, span: "md:col-span-6", ratio: "aspect-[16/10]", label: "Living & Kitchen" },
  ];
  const [lightbox, setLightbox] = useState(null);
  return (
    <section id="gallery" className="px-6 lg:px-12 py-28 lg:py-36 max-w-[1400px] mx-auto">
      <Reveal className="mb-14 max-w-2xl">
        <span className="overline text-[#B86B52]">{t("The Gallery")}</span>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("Inside Casa de León")}</h2>
      </Reveal>
      <div className="grid md:grid-cols-12 gap-4 lg:gap-6">
        {items.map((it, i) => (
          <Reveal key={i} delay={(i % 2) * 0.08} className={it.span}>
            <button onClick={() => setLightbox(i)} data-testid={`casa-gallery-item-${i}`}
              className="group relative overflow-hidden rounded-sm block w-full cursor-pointer">
              <img src={it.src} alt={it.label} className={`w-full ${it.ratio} object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="absolute bottom-4 left-5 text-white overline opacity-0 group-hover:opacity-100 transition-opacity duration-500">{t(it.label)} · {t("View")}</span>
            </button>
          </Reveal>
        ))}
      </div>
      <Lightbox images={items} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />
    </section>
  );
}

function Interiors() {
  const { t } = useLang();
  const bedrooms = [
    { img: CASA.bedroom1, label: "Garden Bedroom", note: "King bed · private garden outlook · en-suite bath" },
    { img: CASA.bedroom2, label: "Second Bedroom", note: "Queen bed · hand-forged four-poster · bougainvillea view" },
    { img: CASA.bedroom3, label: "Third Bedroom", note: "Queen bed · carved headboard · garden view" },
  ];
  const baths = [
    { img: CASA.bath1, label: "Rain-Shower Bath" },
    { img: CASA.bath2, label: "Travertine Walk-In Shower" },
    { img: CASA.bath3, label: "Marble Vanity Bath" },
  ];
  return (
    <section id="rooms" className="px-6 lg:px-12 py-28 lg:py-36 bg-[#8C9B90]/10">
      <div className="max-w-[1400px] mx-auto">
        <Reveal className="mb-14 max-w-2xl">
          <span className="overline text-[#B86B52]">{t("Inside the Villa")}</span>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("Three bedrooms, three baths")}</h2>
          <p className="text-[#5C5A56] font-light leading-relaxed mt-5">{t("Three restful bedrooms sleep up to six, each dressed in crisp linens and hand-crafted Mexican furnishings, and served by three full marble baths with rain showers.")}</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {bedrooms.map((r, i) => (
            <Reveal key={r.label} delay={i * 0.08}>
              <div className="group relative overflow-hidden rounded-sm">
                <img src={r.img} alt={t(r.label)} className="w-full aspect-[4/5] object-cover transition-transform duration-[1.3s] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <Bed className="w-5 h-5 text-[#E4B58C] mb-2" strokeWidth={1.5} />
                  <div className="text-white font-display text-2xl leading-tight">{t(r.label)}</div>
                  <div className="text-white/80 text-sm font-light mt-1">{t(r.note)}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-16 mb-6 flex items-center gap-3">
          <Bath className="w-5 h-5 text-[#B86B52]" strokeWidth={1.5} />
          <span className="overline text-[#B86B52]">{t("Three spa baths")}</span>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {baths.map((r, i) => (
            <Reveal key={r.label} delay={i * 0.08}>
              <div className="group relative overflow-hidden rounded-sm">
                <img src={r.img} alt={t(r.label)} className="w-full aspect-[4/3] object-cover transition-transform duration-[1.3s] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-5 right-5 text-white font-display text-xl">{t(r.label)}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Amenities() {
  const { t } = useLang();
  const list = [
    ["01", "Grand Panoramic Terrace", Sun], ["02", "Sea of Cortez & Arch Views", Mountain],
    ["03", "Gated Community Security", ShieldCheck], ["04", "Resort-Style Community Pools", Waves],
    ["05", "Full Gourmet Kitchen", Utensils], ["06", "High-Speed Wi-Fi", Wifi],
    ["07", "Central Air Conditioning", Wind], ["08", "Private Parking", Car],
    ["09", "Landscaped Gardens", Trees], ["10", "Sleeps up to Six", Bed],
    ["11", "Portable Crib Available", Baby],
  ];
  return (
    <section id="amenities" className="px-6 lg:px-12 py-28 lg:py-36 max-w-[1400px] mx-auto">
      <div className="grid lg:grid-cols-2 gap-16">
        <Reveal>
          <span className="overline text-[#B86B52]">{t("Amenities")}</span>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918] leading-[1.02]">
            {t("Every comfort of a private Tuscan estate.")}
          </h2>
          <p className="text-[#5C5A56] font-light leading-relaxed mt-6 max-w-md">
            {t("From the grand terrace to the gated grounds and resort-style pools, Casa de León is prepared for an effortless escape.")}
          </p>
          <img src={CASA.terrace} alt="Casa de León grand terrace" className="w-full aspect-[16/10] object-cover rounded-sm mt-10" />
        </Reveal>
        <Reveal delay={0.1}>
          <div>
            {list.map(([num, label, Icon], i) => (
              <div key={num} className="flex items-center gap-6 py-6 border-b border-[#1A1918]/10">
                <span className="font-display text-xl text-[#1A1918]/40 w-8">{num}</span>
                <Icon className="w-5 h-5 text-[#B86B52]" strokeWidth={1.5} />
                <span className="text-lg text-[#1A1918] font-light">{t(label)}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Community() {
  const { t } = useLang();
  return (
    <section id="resort" className="relative h-[75vh] overflow-hidden" data-testid="casa-community">
      <img src={CASA.poolCommunity} alt="Punta Arena community pool" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F2537]/85 via-[#0F2537]/40 to-transparent" />
      <div className="relative z-10 h-full flex items-center px-6 lg:px-12 max-w-[1400px] mx-auto">
        <div className="max-w-xl">
          <span className="overline text-[#E4B58C]">{t("The Community")}</span>
          <h2 className="font-display font-light text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.0] tracking-tight mt-5">{t("Life inside Punta Arena")}</h2>
          <p className="text-white/85 font-light text-lg leading-relaxed mt-6">
            {t("Casa de León sits within Punta Arena, a secure gated community in the hills of El Tezal — Mediterranean architecture, manicured grounds and resort-style pools, minutes from the marina, golf and the beaches of Cabo San Lucas.")}
          </p>
        </div>
      </div>
    </section>
  );
}

function Grounds() {
  const { t } = useLang();
  const shots = [
    { src: CASA.exterior, span: "md:col-span-7", ratio: "aspect-[16/10]", label: "Villa Exterior" },
    { src: CASA.poolSecond, span: "md:col-span-5", ratio: "aspect-[16/10]", label: "Second Pool" },
    { src: CASA.poolCommunity, span: "md:col-span-5", ratio: "aspect-[16/10]", label: "Community Pool" },
    { src: CASA.poolArea, span: "md:col-span-7", ratio: "aspect-[16/10]", label: "Poolside Palapa" },
  ];
  const [lightbox, setLightbox] = useState(null);
  return (
    <section id="grounds" className="px-6 lg:px-12 py-28 lg:py-36 max-w-[1400px] mx-auto" data-testid="casa-grounds">
      <Reveal className="mb-14 max-w-2xl">
        <span className="overline text-[#B86B52]">{t("The Grounds")}</span>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("Two community pools, steps away")}</h2>
        <p className="text-[#5C5A56] font-light leading-relaxed mt-5">{t("Beyond the villa's own front door, the gated grounds of Punta Arena unfold — two beautifully landscaped community pools framed by bougainvillea, palapas and swaying palms, reserved for residents and their guests.")}</p>
      </Reveal>
      <div className="grid md:grid-cols-12 gap-4 lg:gap-5">
        {shots.map((s, i) => (
          <Reveal key={s.label} delay={(i % 2) * 0.08} className={s.span}>
            <button onClick={() => setLightbox(i)} className="group relative overflow-hidden rounded-sm w-full block cursor-pointer" data-testid={`casa-grounds-item-${i}`}>
              <img src={s.src} alt={t(s.label)} className={`w-full ${s.ratio} object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="absolute bottom-4 left-5 text-white overline opacity-0 group-hover:opacity-100 transition-opacity duration-500">{t(s.label)} · {t("View")}</span>
            </button>
          </Reveal>
        ))}
      </div>
      <Lightbox images={shots} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />
    </section>
  );
}

function Terrace() {
  const { t } = useLang();
  return (
    <section className="px-6 lg:px-12 py-28 lg:py-36 max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
      <Reveal delay={0.15} className="lg:order-2">
        <img src={CASA.terraceView} alt="Casa de León grand terrace panoramic view" className="w-full aspect-[4/5] object-cover rounded-sm" />
      </Reveal>
      <Reveal className="lg:order-1">
        <span className="overline text-[#B86B52]">{t("The Grand Terrace")}</span>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 text-[#1A1918]">{t("Where the days unfold")}</h2>
        <p className="text-[#5C5A56] font-light leading-relaxed mt-6">{t("Wide and open to the horizon, the grand terrace frames blue skies, drifting white clouds and the shimmer of the Sea of Cortez — the villa's finest room, and the one with no walls.")}</p>
        <div className="mt-8">
          <img src={CASA.dining} alt="Casa de León dining room" className="w-full aspect-[16/10] object-cover rounded-sm" />
          <p className="text-[#5C5A56] font-light text-sm mt-4 italic">{t("The dining room seats eight or more beneath elegant decor — made for gathering.")}</p>
        </div>
      </Reveal>
    </section>
  );
}

function Location() {
  const { t } = useLang();
  return (
    <section id="location" className="px-6 lg:px-12 py-28 lg:py-36 bg-[#0F2537] text-[#F6F4F0]">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <span className="overline text-[#B86B52]">{t("The Location")}</span>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.02]">
            Punta Arena,<br />El Tezal · Cabo San Lucas
          </h2>
          <p className="text-[#A3A09A] font-light leading-relaxed mt-6 max-w-md">
            {t("Perched in the hills of El Tezal along the Cabo Corridor, Casa de León is minutes from Médano Beach, the marina and El Arco — with world-class golf, dining and Land's End all within easy reach.")}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-12">
            {[["20 min", "To Monuments Beach"], ["15 min", "To the Marina"], ["10 min", "To Golf"], ["45 min", "To Airport"]].map(([a, b]) => (
              <div key={b}>
                <div className="font-display text-3xl lg:text-4xl">{a}</div>
                <div className="text-[#A3A09A] text-sm mt-1 font-light">{t(b)}</div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div data-testid="casa-map" className="rounded-sm overflow-hidden border border-white/10">
            <iframe title="Casa de León · El Tezal, Cabo San Lucas"
              src="https://maps.google.com/maps?q=El%20Tezal%2C%20Cabo%20San%20Lucas%2C%20Baja%20California%20Sur%2C%20Mexico&t=&z=13&ie=UTF8&iwloc=B&output=embed"
              width="100%" height="360" loading="lazy" style={{ border: 0, filter: "grayscale(0.2) contrast(1.05)" }}
              referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  useEffect(() => {
    const handler = () => {
      setForm((f) => ({
        ...f,
        message: t("I'm interested in an extended stay at Casa de León (longer than three months).\n\nLength of stay: \nPreferred start date: \nNumber of guests: \n\nA little about our plans: "),
      }));
      setTimeout(() => {
        const el = document.getElementById("casa-contact-message");
        el?.focus({ preventScroll: true });
        el?.setSelectionRange(el.value.length, el.value.length);
      }, 400);
    };
    window.addEventListener("prefill-extended-stay", handler);
    return () => window.removeEventListener("prefill-extended-stay", handler);
  }, [t]);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { ...form, message: `[Casa de León] ${form.message}`, lang });
      toast.success(t("Message sent — we'll be in touch shortly."));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };
  const cls = "w-full bg-white border border-[#1A1918]/15 rounded-sm px-4 py-3 text-[#1A1918] font-light focus:outline-none focus:border-[#B86B52] transition-colors";
  return (
    <section id="contact" className="px-6 lg:px-12 py-28 lg:py-36 bg-[#8C9B90]/10">
      <div className="max-w-[1000px] mx-auto grid lg:grid-cols-2 gap-16 items-start">
        <Reveal>
          <span className="overline text-[#B86B52]">{t("Questions?")}</span>
          <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight mt-5 text-[#1A1918]">{t("Ask the owner directly")}</h2>
          <p className="text-[#5C5A56] font-light mt-5 leading-relaxed">{t("Planning a special trip or need custom dates? Send a note and hear back personally.")}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="casa-contact-form" className="space-y-4">
            <input required data-testid="casa-contact-name" placeholder={t("Name")} className={cls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input required type="email" data-testid="casa-contact-email" placeholder={t("Email")} className={cls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="casa-contact-phone" placeholder={t("Phone (optional)")} className={cls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <textarea required rows={4} id="casa-contact-message" data-testid="casa-contact-message" placeholder={t("Your message")} className={`${cls} resize-none`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            <button data-testid="casa-contact-submit" disabled={sending} className="w-full py-4 bg-[#2A4759] text-white tracking-wide rounded-sm hover:bg-[#1e3644] transition-colors disabled:opacity-60">
              {sending ? t("Sending…") : t("Send Message")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function CasaGuide() {
  const { t } = useLang();
  const [cats, setCats] = useState([]);
  const imgFor = { dining: GUIDE.dining, beach: GUIDE.beach, water: GUIDE.water };
  useEffect(() => { api.get("/guide", { params: { property: "casa-de-leon" } }).then((r) => setCats(r.data)).catch(() => {}); }, []);
  if (!cats.length) return null;
  return (
    <section id="guide" className="px-6 lg:px-12 py-28 lg:py-36 bg-[#EAE5DC]" data-testid="casa-guide">
      <div className="max-w-[1400px] mx-auto">
        <Reveal className="mb-16 max-w-2xl">
          <span className="overline text-[#B86B52]">{t("The Local Guide")}</span>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("Our favourite corners of Cabo San Lucas")}</h2>
          <p className="text-[#5C5A56] font-light leading-relaxed mt-5">{t("A curated shortlist from the owner — the restaurants, beaches and experiences worth building your trip around.")}</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8">
          {cats.map((c, i) => (
            <Reveal key={c.key || i} delay={i * 0.1}>
              <div className="bg-white border border-[#1A1918]/10 rounded-sm overflow-hidden h-full flex flex-col">
                <div className="relative overflow-hidden">
                  <img src={imgFor[c.key] || GUIDE.beach} alt={t(c.title)} className="w-full aspect-[4/3] object-cover" />
                  <span className="absolute top-4 left-4 overline text-white bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-sm">{t(c.tag)}</span>
                </div>
                <div className="p-7 flex-1">
                  <h3 className="font-display text-2xl text-[#1A1918] mb-5">{t(c.title)}</h3>
                  <ul className="space-y-4">
                    {c.items.map((it, j) => (
                      <li key={j} className="border-b border-[#1A1918]/10 pb-3 last:border-0">
                        <div className="text-[#1A1918] font-medium">{t(it.name)}</div>
                        {it.desc && <div className="text-[#5C5A56] text-sm font-light">{t(it.desc)}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CasaReviews() {
  const { t } = useLang();
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get("/reviews", { params: { property: "casa-de-leon" } }).then((r) => setReviews(r.data)).catch(() => {}); }, []);
  if (!reviews.length) return null;
  return (
    <section id="reviews" className="px-6 lg:px-12 py-28 lg:py-36 max-w-[1400px] mx-auto" data-testid="casa-reviews">
      <Reveal className="mb-16 max-w-2xl">
        <span className="overline text-[#B86B52]">{t("Guest Stories")}</span>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("Loved by every guest")}</h2>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-8">
        {reviews.map((r, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="bg-white border border-[#1A1918]/10 rounded-sm p-8 h-full flex flex-col">
              <div className="text-[#B86B52] mb-5 tracking-widest">{"★".repeat(r.rating)}</div>
              <p className="font-display text-xl text-[#1A1918] leading-relaxed italic flex-1">"{r.text}"</p>
              <div className="mt-6 pt-5 border-t border-[#1A1918]/10">
                <div className="text-[#1A1918] font-medium">{r.name}</div>
                <div className="text-[#5C5A56] text-sm font-light">{r.location} · {r.date}</div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CasaPolicies() {
  const { t } = useLang();
  const cols = [
    {
      tag: "Cancellation", title: "Cancellation Policy",
      intro: "Standard vacation-rental terms, in line with Airbnb & VRBO:",
      items: [
        ["30+ days before check-in", "Full refund of all nightly charges and the cleaning fee."],
        ["14–30 days before check-in", "50% refund of nightly charges; cleaning fee fully refunded."],
        ["Within 14 days of check-in", "No refund of nightly charges; cleaning fee refunded if not yet incurred."],
        ["No-shows & early departures", "Charged as booked; no refund for unused nights."],
      ],
      note: "Refunds are processed to the original payment method within 5–10 business days. Dates are only held once payment is complete.",
    },
    {
      tag: "House Rules", title: "House Rules",
      intro: "By booking, guests agree to the following:",
      items: [
        ["Check-in 3:00 PM · Check-out 11:00 AM", "Late departures may incur an additional night."],
        ["Maximum 6 guests", "Only registered guests may stay overnight; no unregistered visitors."],
        ["No parties or events", "This is a quiet residential resort; quiet hours 10 PM–8 AM."],
        ["No smoking indoors", "Smoking permitted only on the open terrace."],
        ["No illegal drugs or narcotics", "The possession, use, manufacture, or distribution of any illegal drugs or controlled substances anywhere on the property is strictly prohibited. Violation results in immediate termination of the stay without refund and may be reported to the authorities."],
        ["Pets by prior approval only", "Contact the owner before booking; unapproved pets incur a fee."],
        ["Care for the home & community", "Follow all Punta Arena community rules; lock up and conserve water/AC when out."],
      ],
    },
    {
      tag: "Damages & Security", title: "Damage Policy & Penalties",
      intro: "Guests are responsible for the condition of the residence:",
      items: [
        ["Refundable damage deposit", "An $500 refundable damage deposit is collected upfront at booking and returned in full within 15 days of checkout, provided there is no damage."],
        ["Accidental damage", "Report promptly. Guests are liable for repair/replacement of damage beyond normal wear, deducted from the deposit or billed if it exceeds the deposit."],
        ["Smoking violation", "$300 deep-cleaning & deodorizing fee."],
        ["Unapproved pet or extra guests", "$250 fee plus any related cleaning or damage costs."],
        ["Lost keys / fobs / excessive mess", "$150 replacement / additional cleaning fee as applicable."],
        ["Missing items or major damage", "Billed at full replacement cost beyond the deposit."],
      ],
    },
  ];
  return (
    <section id="policies" className="px-6 lg:px-12 py-24 lg:py-32 bg-[#EAE5DC]" data-testid="casa-policies">
      <div className="max-w-[1400px] mx-auto">
        <Reveal className="mb-14 max-w-2xl">
          <span className="overline text-[#B86B52]">{t("Before You Book")}</span>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("Policies & agreement")}</h2>
          <p className="text-[#5C5A56] font-light leading-relaxed mt-5">{t("Completing a booking confirms your agreement to the cancellation policy, house rules, and damage terms below.")}</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8">
          {cols.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="bg-white border border-[#1A1918]/10 rounded-sm p-8 h-full">
                <span className="overline text-[#B86B52]">{t(c.tag)}</span>
                <h3 className="font-display text-2xl text-[#1A1918] mt-2 mb-4">{t(c.title)}</h3>
                <p className="text-[#5C5A56] font-light text-sm mb-5">{t(c.intro)}</p>
                <ul className="space-y-4">
                  {c.items.map(([k, v]) => (
                    <li key={k} className="border-b border-[#1A1918]/10 pb-3 last:border-0">
                      <div className="text-[#1A1918] font-medium text-sm">{t(k)}</div>
                      <div className="text-[#5C5A56] text-sm font-light mt-0.5">{t(v)}</div>
                    </li>
                  ))}
                </ul>
                {c.note && <p className="text-xs text-[#5C5A56] font-light mt-5 italic">{t(c.note)}</p>}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CasaHouseRulesFAQ() {
  const { t } = useLang();
  const rules = [
    ["Check-in", "3:00 PM"], ["Check-out", "11:00 AM"], ["Parking", "Private on-site"],
    ["Guests", "Up to 6"], ["Smoking", "No — outdoor terrace only"], ["Pets", "By request — ask first"],
  ];
  const faqs = [
    ["What are the check-in and check-out times?", "Check-in is from 3:00 PM and check-out is by 11:00 AM. Early check-in or late check-out can sometimes be arranged — just ask after booking."],
    ["Is parking available?", "Yes. Casa de León includes private on-site parking within the gated Punta Arena community."],
    ["How do I get the keys / access?", "You'll receive detailed self check-in instructions and the door code by email a few days before arrival. The Punta Arena community has controlled, gated access."],
    ["How many guests can stay?", "Casa de León comfortably sleeps up to 6 guests across its three bedrooms — ideal for families or groups of friends."],
    ["Are pets allowed?", "Pets may be considered on a case-by-case basis — please contact the owner before booking so we can confirm. Unapproved pets incur a $250 fee."],
    ["What is the cancellation policy?", "Standard vacation-rental terms: cancel 30+ days before check-in for a full refund; cancel 14–30 days before for a 50% refund of nightly charges (cleaning fee always refunded before check-in); within 14 days, nightly charges are non-refundable. No-shows and early departures are charged as booked. Refunds return to your original payment method within 5–10 business days."],
    ["What are the villa rules I'm agreeing to?", "By booking you agree to: check-in 3 PM / check-out 11 AM; maximum 6 registered guests; no parties or events; quiet hours 10 PM–8 AM; no smoking indoors (terrace only); and pets by prior approval only. The possession, use, manufacture, or distribution of any illegal drugs or controlled substances anywhere on the property is strictly prohibited and will result in immediate termination of the stay without refund and possible notification of the authorities. Guests must follow all Punta Arena community rules and care for the home."],
    ["Is there a damage deposit, and how is it refunded?", "Yes. An $500 refundable damage deposit is collected at checkout when you pay through our secure Stripe checkout. After you check out and the home is inspected, the full $500 is refunded to the exact card/payment method you used — within 15 days of departure. If there is damage beyond normal wear, the repair/replacement cost is deducted from the deposit and the remainder is returned; costs exceeding the deposit may be billed separately."],
    ["How far is the airport?", "Los Cabos International Airport (SJD) is roughly a 45-minute drive. We're happy to recommend trusted airport transfer options."],
  ];
  return (
    <section id="faq" className="px-6 lg:px-12 py-28 lg:py-36 max-w-[1100px] mx-auto" data-testid="casa-faq">
      <Reveal className="mb-14">
        <span className="overline text-[#B86B52]">{t("Good to Know")}</span>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918]">{t("House rules & FAQ")}</h2>
      </Reveal>
      <div className="grid lg:grid-cols-3 gap-6 mb-16">
        {rules.map(([k, v]) => (
          <Reveal key={k}>
            <div className="flex items-baseline justify-between border-b border-[#1A1918]/15 py-4">
              <span className="text-[#5C5A56] font-light">{t(k)}</span>
              <span className="text-[#1A1918] font-medium text-right">{t(v)}</span>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal>
        <Accordion type="single" collapsible className="w-full" data-testid="casa-faq-accordion">
          {faqs.map(([q, a], i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#1A1918]/15">
              <AccordionTrigger data-testid={`casa-faq-q-${i}`} className="text-left font-display text-xl lg:text-2xl text-[#1A1918] hover:no-underline py-6">{t(q)}</AccordionTrigger>
              <AccordionContent className="text-[#5C5A56] font-light text-base leading-relaxed pb-6">{t(a)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>
  );
}

export default function CasaDeLeon() {
  const { t } = useLang();
  const [property, setProperty] = useState(null);
  useSeo({
    title: "Casa de León · Tuscan Villa in Cabo San Lucas · Derron Luxury",
    description: "Book Casa de León directly — a Tuscan-inspired three-bedroom villa at Punta Arena above Land's End, Cabo San Lucas. Sweeping terrace views, sleeps six.",
    path: "/casa-de-leon",
  });
  useEffect(() => {
    api.get("/property?property=casa-de-leon").then((r) => setProperty(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      <Nav />
      <Hero />
      <Story />
      <Gallery />
      <Interiors />
      <Community />
      <Grounds />
      <Terrace />
      <Amenities />
      <Location />
      <CasaGuide />
      <CasaReviews />
      <section id="booking" className="px-6 lg:px-12 py-28 lg:py-36 max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 [&>*]:min-w-0">
          <Reveal>
            <span className="overline text-[#B86B52]">{t("Reserve Your Stay")}</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 text-[#1A1918] leading-[1.02]">
              {t("Book Casa de León directly")}
            </h2>
            <p className="text-[#5C5A56] font-light mt-6 leading-relaxed max-w-md">
              {t("Real-time availability, transparent pricing, and secure card payment. No middleman, no platform surcharge — just your dates and the Baja sun.")}
            </p>
            <img src={CASA.terrace} alt="Casa de León terrace" className="w-full aspect-[16/10] object-cover rounded-sm mt-10" />
            <div data-testid="casa-extended-stay-note" className="mt-12 border-t border-[#1A1918]/10 pt-10 max-w-md">
              <span className="overline text-[#B86B52]">{t("Extended Escapes")}</span>
              <h3 className="font-display font-light text-3xl lg:text-4xl text-[#1A1918] leading-snug mt-4">
                {t("Staying beyond three months?")}
              </h3>
              <p className="text-[#5C5A56] font-light mt-5 leading-relaxed">
                {t("For seasons rather than stays, Florencia extends privately arranged rates reserved for long-term guests. Share your dates and desired length of stay, and she will compose an offer tailored to your extended escape.")}
              </p>
              <button data-testid="casa-extended-stay-button"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("prefill-extended-stay"));
                  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="group inline-flex items-center gap-2.5 mt-7 px-7 py-3.5 border border-[#2A4759] text-[#2A4759] text-sm tracking-wide rounded-sm hover:bg-[#2A4759] hover:text-white transition-colors">
                {t("Enquire about a longer stay")}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
              </button>
            </div>
          </Reveal>
          <div className="lg:sticky lg:top-28 h-fit">
            <BookingWidget property={property} />
          </div>
        </div>
      </section>
      <CasaPolicies />
      <CasaHouseRulesFAQ />
      <MeetFlorencia />
      <Contact />
      <Footer />
    </>
  );
}
